import {
  BasicBotData,
  BotType,
  WorkspaceType,
  LanguageNameSpaces,
} from "@shared/types";
import { ColumnsType, ColumnType } from "antd/es/table";
import Table from "common/Table/Table";
import { getBotExtraDetailsRoute } from "utils/routes";
import { useHistory } from "react-router-dom";
import { SORT_ORDER_MAPPING } from "common/constants";
import React, { useContext } from "react";
import Card from "common/Boxes/Card";
import IconWithFallback from "Global/IconWithFallback";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import MarginData from "features/bots/MyBots/MarginData";
import NextOrder from "features/bots/MyBots/NextOrder";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import { Clock } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import NewLookNextOrder from "features/bots/NewLook/NewLookNextOrder";
import BotChange from "features/bots/common/BotChange";
import { getTableOnChangeFunction } from "common/Table/utils";
import BotActions from "features/bots/common/BotActions";
import styled, { useTheme } from "styled-components";
import useMeasure from "utils/hooks/useMeasure";
import { OrderTypes } from "@shared/features/trades/common/types";
import { typography } from "@shared/common/Themes/typography";
import { ICON_TYPES } from "@shared/common/constants";
import { MyBotsSort } from "@shared/features/bots/types";
import {
  getBotAmountDisplay,
  isGridPnlBotType,
} from "@shared/features/bots/utils";
import useTableRowsSelector from "utils/hooks/useTableRowsSelector";
import { BotsBulkActionsContext } from "@shared/features/bots/BotsBulkActionsProvider";
import SelectAllBotsDropdown from "features/bots/MyBots/SelectAllBotsDropdown";
import BotStatusTag from "features/bots/common/BotStatus";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { AccountObjectType } from "@shared/features/settings/types";
import { formatDateRelative } from "@shared/utils/date";
import Checkbox from "common/Checkbox/Checkbox";
import BotMainIcon from "features/bots/MyBots/common/BotMainIcon";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import InactiveGroupIcon from "features/configuration/common/InactiveGroupIcon.tsx";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants.ts";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useCanManageBotType } from "@shared/features/bots/hooks";
import useNewLookBotsTableColumns from "features/bots/NewLook/useNewLookBotsTableColumns";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

interface BotsTableProps {
  emptyState?: React.JSX.Element;
}

const BotsTable = ({ emptyState }: BotsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const history = useHistory();
  const {
    bots,
    unfilteredBots,
    botType,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    isFetching,
    isChannelConfiguration,
  } = useContext(MyBotsPageContext)!;
  const {
    selectedBotsIDs,
    selectedBotStatus,
    setSelectedBotsIDs,
    onSelectUnselectAllPress,
    isLoading: isLoadingBulkAction,
  } = useContext(BotsBulkActionsContext)!;
  const { getInvalidAccountData } = useInvalidAccount();
  const workspace = useSelector(selectWorkspace);
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const isHedgingBot = botType === BotType.HEDGING;
  const useGridPnl = isGridPnlBotType(botType);
  const canManageBotType = useCanManageBotType(botType);

  let maxColumnWidth: number | undefined = undefined;
  if (width < 1400) maxColumnWidth = 300;
  if (width < 1230) maxColumnWidth = 200;
  if (width < 1100) maxColumnWidth = 150;

  const getSelectAllCheckbox = (
    isAllRowsSelected: boolean,
    indeterminate: boolean
  ) => {
    return (
      <SelectAllBotsDropdown
        trigger={
          <Checkbox
            checked={isAllRowsSelected}
            indeterminate={indeterminate}
            onChange={(e: CheckboxChangeEvent) => {
              e.stopPropagation();
              onSelectUnselectAllPress();
            }}
          />
        }
      />
    );
  };

  const { tableRowProps } = useTableRowsSelector<BasicBotData, number>({
    selectedRows: selectedBotsIDs,
    setSelectedRows: setSelectedBotsIDs,
    getKey: (botData) => botData.id,
    allRows: unfilteredBots,
    filteredRows: bots,
    getSelectAllCheckbox,
    extraData: selectedBotStatus,
  });

  const showStatusColumn = width > 1500;
  const newLookColumns = useNewLookBotsTableColumns({
    botType,
    sortBy,
    sortDirection,
    isChannelConfiguration,
    workspace,
  });

  const getNameColumn = (botData: BasicBotData) => {
    const accountData = getInvalidAccountData(botData.client_id);

    return (
      <Flex $justifyContent="flex-start">
        <IconWithFallback
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={botData.exchange}
          iconStyle={EXCHANGE_ICON_STYLE}
        />
        <Tooltip
          placement="bottom"
          title={botData.exchange}
          trigger={accountData.isInvalidOrInactive ? [] : undefined}
        >
          <div>
            <Text $ellipsis={true}>{botData.name}</Text>
            <InactiveAccountIcon
              hideIcon={true}
              accountId={botData.client_id}
              objectType={AccountObjectType.ACCOUNT}
              validColor={theme.colors.secondaryGrey}
            >
              <Text $size={typography.fontSizes.S} style={{ color: "inherit" }}>
                {botData.client_name}
              </Text>
            </InactiveAccountIcon>
          </div>
        </Tooltip>
      </Flex>
    );
  };

  const getSymbolColumn = (botData: BasicBotData) => {
    const symbol =
      !botData.symbols &&
      [BotType.TRADING_VIEW, BotType.TRADING_VIEW_SIGNALS].includes(
        botData.bot_type
      )
        ? TV_ALERT_SYMBOL
        : botData.symbols?.[0];
    return (
      <Flex $justifyContent="flex-start">
        <BotMainIcon botData={botData} showOneClickDefaultIcon={true} />
        <div style={{ maxWidth: maxColumnWidth }}>
          <Flex $justifyContent="flex-start">
            <Text $ellipsis={true}>{symbol}</Text>
            {!showStatusColumn && (
              <BotStatusTag bot={botData} hideText={true} hideBgColor={true} />
            )}
          </Flex>
          {isTradingViewSignalsBot && <MarginData botData={botData} />}
        </div>
      </Flex>
    );
  };

  const getGroupColumn = (botData: BasicBotData) => {
    return (
      <Flex $justifyContent="flex-start">
        <BotMainIcon
          botData={botData}
          showOneClickDefaultIcon={isSignalsBot}
          isGroupIcon={true}
        />
        <div style={{ maxWidth: maxColumnWidth }}>
          <Flex $justifyContent="flex-start">
            <InactiveGroupIcon
              isInactive={botData.sub_group_is_active === false}
              style={{ flex: 1, overflow: "hidden" }}
            >
              <Text $ellipsis={true} style={{ color: "inherit" }}>
                {botData.sub_group_name}
              </Text>
            </InactiveGroupIcon>
            {!showStatusColumn && !isTradingViewSignalsBot && (
              <BotStatusTag bot={botData} hideText={true} hideBgColor={true} />
            )}
          </Flex>
          {!isTradingViewSignalsBot && <MarginData botData={botData} />}
        </div>
      </Flex>
    );
  };

  const exchangesColumn: ColumnType<BasicBotData> = {
    title: "Exchanges",
    dataIndex: "exchanges",
    key: "exchanges",
    showSorterTooltip: false,
    render: (_, botData) => (
      <ExchangesIconsGroup
        exchanges={botData.exchanges}
        size={28}
        maxCount={7}
      />
    ),
  };

  const symbolColumn: ColumnType<BasicBotData> = {
    title: t("labels.symbol"),
    dataIndex: "symbols",
    key: "symbols",
    showSorterTooltip: false,
    sortOrder:
      sortBy === MyBotsSort.SYMBOL
        ? SORT_ORDER_MAPPING[sortDirection]
        : undefined,
    sorter: true,
    render: (_, botData) => getSymbolColumn(botData),
  };

  const groupColumn: ColumnType<BasicBotData> = {
    title: t("labels.group"),
    dataIndex: "group",
    key: "group",
    showSorterTooltip: false,
    sortOrder:
      sortBy === MyBotsSort.GROUP
        ? SORT_ORDER_MAPPING[sortDirection]
        : undefined,
    sorter: true,
    render: (_, botData) => getGroupColumn(botData),
  };

  let columns: ColumnsType<BasicBotData> = [
    isSignalsBot ? groupColumn : symbolColumn,
    {
      title: t("labels.name"),
      dataIndex: "name",
      key: "name",
      showSorterTooltip: false,
      sortOrder:
        sortBy === MyBotsSort.NAME
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: true,
      render: (_, botData) => getNameColumn(botData),
    },
  ];

  if (isTradingViewSignalsBot) {
    columns.splice(3, 0, exchangesColumn);
    if (workspace !== WorkspaceType.ADMIN) {
      columns.splice(2, 0, groupColumn);
    }
  }

  const singleTradeBotsColumns: ColumnsType<BasicBotData> = [
    {
      title: t("labels.last_entry"),
      dataIndex: "last_entry",
      showSorterTooltip: false,
      sortOrder:
        sortBy === MyBotsSort.LAST_ENTRY
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, botData) => {
        const lastEntry = botData.active_trade?.last_entry;
        return lastEntry ? (
          <>
            {`#${lastEntry?.target_number}/${lastEntry?.number_of_targets}`}
            <Text
              $size={typography.fontSizes.S}
              style={{
                display: "inline",
                marginLeft: 10,
              }}
            >
              <LookIcon
                classic={<ClockCircleOutlined style={{ marginRight: 4 }} />}
                newLook={
                  <Clock
                    size={12}
                    strokeWidth={1.75}
                    style={{ marginRight: 4, verticalAlign: -1 }}
                  />
                }
              />
              {lastEntry?.time_passed} {t("common.ago")}
            </Text>
          </>
        ) : (
          <Text type="secondary">{t("common.na")}</Text>
        );
      },
      sorter: true,
    },
    {
      title: t("labels.next_entry_or_stop"),
      dataIndex: "next_entry_or_stop",
      showSorterTooltip: false,
      sortOrder:
        sortBy === MyBotsSort.NEXT_ENTRY_OR_STOP
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, botData) => {
        const nextEntry = botData.active_trade?.next_entry;
        const nextOrderProps = {
          symbol: botData.symbols?.[0],
          exchange: botData.exchange,
          nextOrder: nextEntry || botData.active_trade?.next_stop,
          orderType: OrderTypes.ENTRY,
        };
        // New look: a stop is shown as a stop (the classic cell keeps its
        // entry styling for both).
        return theme.isNewLook ? (
          <NewLookNextOrder
            {...nextOrderProps}
            orderType={nextEntry ? OrderTypes.ENTRY : OrderTypes.STOP}
          />
        ) : (
          <NextOrder {...nextOrderProps} />
        );
      },
      sorter: true,
    },
    {
      title: t("labels.take_profit"),
      dataIndex: "next_take_profit",
      showSorterTooltip: false,
      sortOrder:
        sortBy === MyBotsSort.NEXT_TAKE_PROFIT
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: true,
      render: (_, botData) => {
        const nextOrderProps = {
          symbol: botData.symbols?.[0],
          exchange: botData.exchange,
          nextOrder: botData.active_trade?.next_take_profit,
          orderType: OrderTypes.TAKE_PROFIT,
        };
        return theme.isNewLook ? (
          <NewLookNextOrder {...nextOrderProps} />
        ) : (
          <NextOrder {...nextOrderProps} />
        );
      },
    },
  ];

  const multiTradeBotsColumns: ColumnsType<BasicBotData> = [
    {
      title: t("labels.open_trades"),
      dataIndex: "number_of_open_trades",
      sortOrder:
        sortBy === MyBotsSort.OPEN_TRADES
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      showSorterTooltip: false,
      sorter: true,
      render: (_, botData) => (
        <Flex
          $justifyContent="flex-start"
          style={theme.isNewLook ? NEW_LOOK_COUNT_AND_CHANGE : undefined}
        >
          <Text>{botData.number_of_open_trades}</Text>
          <BotChange botData={botData} isOpenTrades={true} />
        </Flex>
      ),
    },
    {
      title: t("labels.last_trade_opened"),
      dataIndex: "last_trade",
      sortOrder:
        sortBy === MyBotsSort.LAST_TRADE_OPENED
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      showSorterTooltip: false,
      sorter: true,
      render: (_, botData) => (
        <Text>
          {botData.last_trade
            ? formatDateRelative(botData.last_trade, true, true, true)
            : t("common.na")}
        </Text>
      ),
    },
  ];

  const pnlColumn: ColumnType<BasicBotData> = useGridPnl
    ? {
        title: t("labels.realized_pnl"),
        dataIndex: "realized_pnl",
        sortOrder:
          sortBy === MyBotsSort.REALIZED_PNL
            ? SORT_ORDER_MAPPING[sortDirection]
            : undefined,
        showSorterTooltip: false,
        sorter: true,
        render: (_, botData) => (
          <BotChange botData={botData} isRealizedPnl={true} />
        ),
      }
    : {
        title: t("labels.closed_trades"),
        dataIndex: "change",
        sortOrder:
          sortBy === MyBotsSort.TOTAL_PROFIT
            ? SORT_ORDER_MAPPING[sortDirection]
            : undefined,
        showSorterTooltip: false,
        sorter: true,
        render: (_, botData) => (
          <Flex
            $justifyContent="flex-start"
            style={theme.isNewLook ? NEW_LOOK_COUNT_AND_CHANGE : undefined}
          >
            <Text>{botData.number_of_closed_trades}</Text>
            <BotChange botData={botData} />
          </Flex>
        ),
      };

  if (!isChannelConfiguration && !isTradingViewSignalsBot) {
    const tradesColumns =
      isSignalsBot || isTradingViewBot
        ? multiTradeBotsColumns
        : singleTradeBotsColumns;
    columns = columns.concat([
      {
        title: t("labels.amount"),
        dataIndex: "amount",
        showSorterTooltip: false,
        sortOrder:
          sortBy === MyBotsSort.AMOUNT
            ? SORT_ORDER_MAPPING[sortDirection]
            : undefined,
        sorter: true,
        render: (_, botData) => <Text>{getBotAmountDisplay(botData)}</Text>,
      },
      pnlColumn,
      ...(isHedgingBot ? [] : tradesColumns),
    ]);
  }

  if (theme.isNewLook) {
    // A copy: the hook's result may be memoized (React Compiler), so it
    // must not be mutated by the push below.
    columns = [...newLookColumns];
  }

  columns.push({
    title: "",
    dataIndex: "id",
    key: "action",
    width: 40,
    render: (_, botData) => (
      <ActionsWrapper onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <BotActions bot={botData} verticalEllipsis={true} />
      </ActionsWrapper>
    ),
  });

  if (showStatusColumn && !theme.isNewLook) {
    columns.splice(1, 0, {
      title: t("labels.status"),
      dataIndex: "status",
      key: "status",
      render: (_, botData) => <BotStatusTag bot={botData} isTable={true} />,
    });
  }

  const openBotExtraDetailsDrawer = (botData: BasicBotData) => {
    history.push(
      getBotExtraDetailsRoute(location.pathname, botData.id.toString())
    );
  };

  const handleChange = getTableOnChangeFunction<MyBotsSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT
  );

  const table = (
    <Table<BasicBotData>
      columns={columns}
      dataSource={bots}
      loading={isFetching || isLoadingBulkAction}
      rowClassName="u-cursorPointer"
      onRow={(botData) => ({
        onClick: () => openBotExtraDetailsDrawer(botData),
        // New look: rows open with the keyboard too.
        ...(theme.isNewLook && {
          tabIndex: 0,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openBotExtraDetailsDrawer(botData);
            }
          },
        }),
      })}
      onChange={handleChange}
      rowKey="id"
      itemsType={BOTS}
      rowSelection={canManageBotType ? tableRowProps : undefined}
      locale={{
        emptyText: emptyState,
      }}
    />
  );

  if (theme.isNewLook) {
    // Edge to edge in the list panel (see NewLookBotsListHeader).
    return <NewLookTable ref={measureRef}>{table}</NewLookTable>;
  }

  return (
    <Card style={CARD_STYLE} ref={measureRef}>
      {table}
    </Card>
  );
};

export default React.memo(BotsTable);

const CARD_STYLE = { overflow: "visible" } as const;

const BOTS = "bots";
const EXCHANGE_ICON_STYLE = { width: 24, height: 24, marginRight: 12 };
const NEW_LOOK_COUNT_AND_CHANGE = { columnGap: 10 };

const FIELD_NAME_TO_SORT = {
  name: MyBotsSort.NAME,
  last_entry: MyBotsSort.LAST_ENTRY,
  next_take_profit: MyBotsSort.NEXT_TAKE_PROFIT,
  next_entry_or_stop: MyBotsSort.NEXT_ENTRY_OR_STOP,
  change: MyBotsSort.TOTAL_PROFIT,
  symbols: MyBotsSort.SYMBOL,
  group: MyBotsSort.GROUP,
  amount: MyBotsSort.AMOUNT,
  last_trade: MyBotsSort.LAST_TRADE_OPENED,
  number_of_open_trades: MyBotsSort.OPEN_TRADES,
  realized_pnl: MyBotsSort.REALIZED_PNL,
};

const ActionsWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// New look: the table runs edge to edge in its panel, the rows 20px in from
// the panel's sides; keyboard focus shows as an inset ring.
const NewLookTable = styled.div`
  overflow-x: auto;
  .ant-table {
    background: transparent;
  }
  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    padding-left: 20px;
  }
  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr > td:last-child {
    padding-right: 12px;
  }
  .ant-table-tbody > tr > td {
    padding-top: 10px;
    padding-bottom: 10px;
    height: 60px;
  }
  .ant-table-tbody > tr:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: -2px;
  }
  .ant-table-tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: 12px;
  }
  .ant-table-tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: 12px;
  }
`;
